import { useMutation } from '@apollo/client';
import styled from "@emotion/styled";
import React, { useState } from 'react';
import { i18n, RootState, showMessage, useAppDispatch, useAppSelector } from 'src/common';
import { checkAndApplyVoucherCode, removeVoucherCode } from 'src/common/api/apollo/supplementary';
import { colors } from 'src/theme';
import { Button } from '../buttons';
import { Flex } from '../grid';
import { InputText } from '../inputs/inputText.input';
import { Loader } from '../other';
import { Text } from '../typography';
import { Card } from './card';

const CardStyle = styled(Card)({
  borderWidth: 1,
  borderStyle: 'dashed',
  borderColor: colors.border3,

  '&:hover': {
    borderColor: colors.primary
  }
})

const SuccessMessage = styled.div({
  color: colors.success,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '1.14',
  marginBottom: 0,
  paddingTop: '4px',
  transition: 'all 0.4s ease',
});

const InputWrapper = styled.div({
  flex: 1
})

const InputTextStyle = styled(InputText)<{hasAppliedCode?: boolean}>(({hasAppliedCode}) => ({
  background: colors.background7,
  borderColor: hasAppliedCode ? colors.success : colors.border4,
  color: colors.text4,
  '&:disabled': {
    borderColor: hasAppliedCode ? colors.success : colors.border4
  }
}))

export interface VoucherCodeCardProps {
  offerId: string;
  appliedVoucherCode?: string | null;
}

export const VoucherCodeCard: React.FC<VoucherCodeCardProps> = ({ offerId, appliedVoucherCode }) => {
  const flowType = useAppSelector((state: RootState) => state.state.flow.flowType);
  const dispatch = useAppDispatch();

  const [voucherCode, setVoucherCode] = useState<string>(appliedVoucherCode || '');

  const [checkAndApplyVoucherCodeMutation, { loading: applyLoading }] = useMutation(checkAndApplyVoucherCode);
  const [removeVoucherCodeMutation, { loading: removeLoading }] = useMutation(removeVoucherCode);
  
  const handleSubmit = () => {
    checkAndApplyVoucherCodeMutation({
      variables: { offerId, voucherCode },
      refetchQueries: [
        'checkoutHealthOfferPaymentOptions'
      ],
      awaitRefetchQueries: true,
      onError: (error) => {
        dispatch(showMessage({ message: i18n.t('components.cards.voucherCodeCard.error'), type: 'error', duration: 6500 }));
        console.error('error', error);
      }
    });
  }

  const handleRemove = () => {
    removeVoucherCodeMutation({
      variables: { offerId },
      refetchQueries: [
        'checkoutHealthOfferPaymentOptions'
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        setVoucherCode('');
      }
    });
  }

  if (flowType === 'agent') return null;
  
  if (applyLoading || removeLoading) return <Loader />;

  return (
    <CardStyle radius={12} padding={'20px 40px'} background='transparent'>
      <Flex vertical gap={12}>
        <Text type='base'>{i18n.t('components.cards.voucherCodeCard.title')}</Text>
        <Flex gap={12} align='start'>
          <InputWrapper>
            <InputTextStyle
              value={appliedVoucherCode || voucherCode}
              onChange={(value) => !appliedVoucherCode && setVoucherCode(value)}
              disabled={!!appliedVoucherCode}
              hasAppliedCode={!!appliedVoucherCode}
            />
            {appliedVoucherCode ? <SuccessMessage>{i18n.t('components.cards.voucherCodeCard.successMessage')}</SuccessMessage> : null}
          </InputWrapper>
          <Button
            disabled={!voucherCode}
            onClick={appliedVoucherCode ? handleRemove : handleSubmit}
            btnType='outlinePrimary'
            text={i18n.t(`components.cards.voucherCodeCard.${appliedVoucherCode ? 'removeButtonLabel' : 'buttonLabel'}`)}
          />
        </Flex>
      </Flex>
    </CardStyle>
  )
}
